'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Record } from 'immutable';
import { SimpleDate, fromMoment, toMoment } from 'UIComponents/core/SimpleDate';
import I18n from 'I18n';
import { getMinutesSinceMidnight } from 'AdsUI/lib/Utils';
export const ScheduleTypes = {
  FIXED_DATE: 'FIXED_DATE',
  DAY_MAX: 'DAY_MAX',
  ENGAGEMENT_MAX: 'ENGAGEMENT_MAX',
  VIDEO_VIEWS_MAX: 'VIDEO_VIEWS_MAX',
  NONE: 'NONE',
  CONTACT_MAX_END: 'CONTACT_MAX_END',
  CONTACT_MAX_START: 'CONTACT_MAX_START'
};
const AdSequenceSteps = {
  ATTRACT: 'attract',
  CONVERT: 'convert',
  CLOSE: 'close'
};
export const SCHEDULE_TYPE_TO_SEQUENCE_TYPE = {
  [ScheduleTypes.FIXED_DATE]: 'FIXED_DATE',
  [ScheduleTypes.CONTACT_MAX_START]: 'NUM_OF_CONTACTS',
  [ScheduleTypes.CONTACT_MAX_END]: 'NUM_OF_CONTACTS',
  [ScheduleTypes.ENGAGEMENT_MAX]: 'NUM_OF_ENGAGEMENTS',
  [ScheduleTypes.VIDEO_VIEWS_MAX]: 'NUM_OF_VIDEO_VIEWS',
  [ScheduleTypes.NONE]: 'NO_END_DATE',
  [ScheduleTypes.DAY_MAX]: 'FIXED_NUM_OF_DAYS'
};
const SEQUENCE_END_TYPE_TO_SCHEDULE_TYPE = {
  NO_END_DATE: ScheduleTypes.NONE,
  NUM_OF_CONTACTS: ScheduleTypes.CONTACT_MAX_END,
  FIXED_NUM_OF_DAYS: ScheduleTypes.DAY_MAX,
  NUM_OF_ENGAGEMENTS: ScheduleTypes.ENGAGEMENT_MAX,
  NUM_OF_VIDEO_VIEWS: ScheduleTypes.VIDEO_VIEWS_MAX,
  FIXED_DATE: ScheduleTypes.FIXED_DATE
};
const SEQUENCE_START_TYPE_TO_SCHEDULE_TYPE = {
  NO_END_DATE: ScheduleTypes.NONE,
  NUM_OF_CONTACTS: ScheduleTypes.CONTACT_MAX_START,
  FIXED_NUM_OF_DAYS: ScheduleTypes.DAY_MAX,
  NUM_OF_ENGAGEMENTS: ScheduleTypes.ENGAGEMENT_MAX,
  NUM_OF_VIDEO_VIEWS: ScheduleTypes.VIDEO_VIEWS_MAX,
  FIXED_DATE: ScheduleTypes.FIXED_DATE
};
const SCHEDULE_BY_STEP = {
  attract: [['attractAdEndType', 'endDateType'], ['numOfEngagementsEndAttractAd', 'engagementMax'], ['numOfDaysEndAttractAd', 'dayMax']],
  convert: [['convertAdEndType', 'endDateType'], ['convertAdStartType', 'startDateType'], ['numOfContactsEndConvertAd', 'contactMaxEnd'], ['numOfEngagementsStartConvertAd', 'engagementMax'], ['numOfDaysEndConvertAd', 'dayMax']],
  close: [['closeAdEndType', 'endDateType'], ['closeAdStartType', 'startDateType'], ['numOfContactsStartCloseAd', 'contactMaxStart'], ['numOfContactsEndCloseAd', 'contactMaxEnd'], ['numOfDaysEndCloseAd', 'dayMax']]
};
const ScheduleRecord = Record({
  startDateType: ScheduleTypes.FIXED_DATE,
  endDateType: ScheduleTypes.FIXED_DATE,
  contactMaxStart: null,
  contactMaxEnd: null,
  dayMax: null,
  engagementMax: null,
  startDate: null,
  startTime: null,
  endDate: null,
  endTime: null,
  startTimeMillis: null,
  endTimeMillis: null,
  disableStartType: false,

  getEndMoment() {
    return this.endDate && toMoment(this.endDate).add(this.endTime, 'minutes');
  },

  getRequestData() {
    return {
      startTimeMillis: toMoment(this.startDate).add(this.startTime, 'minutes').valueOf(),
      endTimeMillis: this.endDate && toMoment(this.endDate).add(this.endTime, 'minutes').valueOf()
    };
  },

  getChangedRequestData(originalSchedule) {
    const originalRequestData = originalSchedule.getRequestData();
    const {
      startTimeMillis,
      endTimeMillis
    } = this.getRequestData();
    const startChanged = startTimeMillis !== originalRequestData.startTimeMillis;
    const endChanged = endTimeMillis !== originalRequestData.endTimeMillis;
    return Object.assign({}, startChanged && {
      startTimeMillis
    }, {}, endChanged && {
      endTimeMillis
    });
  },

  getSequenceRequestData() {
    const data = {
      adSequenceStartType: SCHEDULE_TYPE_TO_SEQUENCE_TYPE[this.startDateType],
      adSequenceEndType: SCHEDULE_TYPE_TO_SEQUENCE_TYPE[this.endDateType]
    };

    if (this.startDateType === ScheduleTypes.FIXED_DATE) {
      data.startTimeMillis = toMoment(this.startDate).add(this.startTime, 'minutes').valueOf();
    }

    if (this.startDateType === ScheduleTypes.VIDEO_VIEWS_MAX || this.startDateType === ScheduleTypes.ENGAGEMENT_MAX) {
      data.numOfEngagementsToStart = this.engagementMax;
    }

    if (this.startDateType === ScheduleTypes.CONTACT_MAX_START) {
      data.numOfContactsToStart = this.contactMaxStart;
    }

    if (this.endDateType === ScheduleTypes.VIDEO_VIEWS_MAX || this.endDateType === ScheduleTypes.ENGAGEMENT_MAX) {
      data.maxNumOfEngagements = this.engagementMax;
    }

    if (this.endDateType === ScheduleTypes.DAY_MAX) {
      data.numOfDaysToRun = this.dayMax;
    }

    if (this.endDateType === ScheduleTypes.CONTACT_MAX_END) {
      data.numOfContactsForEnd = this.contactMaxEnd;
    }

    return data;
  }

});
export const fromEdit = (changes, ad, step) => {
  const {
    startDateType,
    endDateType,
    contactMaxEnd,
    contactMaxStart,
    dayMax,
    engagementMax,
    startDate,
    startTime
  } = changes;
  let adSequenceStartType;
  let adSequenceEndType;
  let startTimeMillis;
  let maxNumOfEngagements;
  let numOfEngagementsToStart;
  let numOfContactsToStart;
  let numOfContactsForEnd;
  let numOfDaysToRun;

  if (startDateType || startDate || startTime || contactMaxStart && step === AdSequenceSteps.CLOSE || engagementMax && step !== AdSequenceSteps.ATTRACT) {
    adSequenceStartType = startDateType && SCHEDULE_TYPE_TO_SEQUENCE_TYPE[startDateType] || SCHEDULE_TYPE_TO_SEQUENCE_TYPE[ad.get('startDateType')];

    if (adSequenceStartType === SCHEDULE_TYPE_TO_SEQUENCE_TYPE[ScheduleTypes.FIXED_DATE]) {
      startTimeMillis = toMoment(startDate || ad.get('startDate')).add(startTime || ad.get('startTime'), 'minutes').valueOf();
    } else if (step === AdSequenceSteps.CONVERT) {
      numOfEngagementsToStart = engagementMax || ad.get('engagementMax');
    } else if (step === AdSequenceSteps.CLOSE) {
      if (adSequenceStartType === SCHEDULE_TYPE_TO_SEQUENCE_TYPE[ScheduleTypes.CONTACT_MAX_START]) {
        numOfContactsToStart = contactMaxStart || ad.get('contactMaxStart');
      } else {
        numOfContactsToStart = engagementMax || ad.get('engagementMax');
      }
    }
  }

  if (endDateType || dayMax || contactMaxEnd || engagementMax && step === AdSequenceSteps.ATTRACT) {
    adSequenceEndType = endDateType && SCHEDULE_TYPE_TO_SEQUENCE_TYPE[endDateType] || SCHEDULE_TYPE_TO_SEQUENCE_TYPE[ad.get('endDateType')];

    if (adSequenceEndType === SCHEDULE_TYPE_TO_SEQUENCE_TYPE[ScheduleTypes.DAY_MAX]) {
      numOfDaysToRun = dayMax || ad.get('dayMax');
    } else if (step === AdSequenceSteps.ATTRACT) {
      maxNumOfEngagements = engagementMax || ad.get('engagementMax');
    } else {
      numOfContactsForEnd = contactMaxEnd || ad.get('contactMaxEnd');
    }
  }

  const data = Object.assign({}, adSequenceStartType && {
    adSequenceStartType
  }, {}, adSequenceEndType && {
    adSequenceEndType
  }, {}, startTimeMillis && {
    startTimeMillis
  }, {}, maxNumOfEngagements && {
    maxNumOfEngagements
  }, {}, numOfEngagementsToStart && {
    numOfEngagementsToStart
  }, {}, numOfContactsToStart && {
    numOfContactsToStart
  }, {}, numOfContactsForEnd && {
    numOfContactsForEnd
  }, {}, numOfDaysToRun && {
    numOfDaysToRun
  });
  return data;
};
export const fromMillis = (startTimeMillis, endTimeMillis) => {
  const startMoment = I18n.moment(startTimeMillis);
  const startDate = fromMoment(startMoment);
  const startTime = getMinutesSinceMidnight(startMoment);

  if (endTimeMillis) {
    const endMoment = I18n.moment(endTimeMillis);
    const endDate = fromMoment(endMoment);
    const endTime = getMinutesSinceMidnight(endMoment);
    return ScheduleRecord({
      startDate,
      startTime,
      endDate,
      endTime,
      startTimeMillis,
      // We need this for LinkedIn management
      endTimeMillis
    });
  }

  return ScheduleRecord({
    startDate,
    startTime,
    endDate: null,
    endTime: 0
  });
};
export const fromPublished = (adSequence, step, startTime) => {
  const schedule = SCHEDULE_BY_STEP[step].reduce((_schedule, [key, param]) => {
    if (param === 'endDateType') {
      _schedule[param] = SEQUENCE_END_TYPE_TO_SCHEDULE_TYPE[adSequence[key]];
    } else if (param === 'startDateType') {
      _schedule[param] = SEQUENCE_START_TYPE_TO_SCHEDULE_TYPE[adSequence[key]];
    } else {
      _schedule[param] = adSequence[key];
    }

    return _schedule;
  }, {});
  return ScheduleRecord(Object.assign({}, schedule, {}, startTime ? {
    startDate: fromMoment(I18n.moment(startTime)),
    startTime: getMinutesSinceMidnight(I18n.moment(startTime))
  } : {}, {
    disableStartType: step !== AdSequenceSteps.ATTRACT && adSequence[`${step}AdStartType`] !== SCHEDULE_TYPE_TO_SEQUENCE_TYPE[ScheduleTypes.FIXED_DATE]
  }));
};
export const fromDraft = (_ref) => {
  let {
    startDate,
    startTime,
    endDate,
    endTime
  } = _ref,
      params = _objectWithoutPropertiesLoose(_ref, ["startDate", "startTime", "endDate", "endTime"]);

  const minimumStartMoment = I18n.moment().add(1, 'hours');
  const defaultTime = getMinutesSinceMidnight(minimumStartMoment);
  const startMoment = toMoment(startDate).add(startTime, 'minutes');
  const endMoment = endDate && toMoment(endDate).add(endTime, 'minutes');
  return ScheduleRecord(Object.assign({}, params, {
    startDate: !startMoment._isValid || minimumStartMoment > startMoment ? fromMoment(minimumStartMoment) : SimpleDate(startDate.year, startDate.month, startDate.date),
    startTime: minimumStartMoment > startMoment ? defaultTime : startTime,
    endDate: endDate && (endMoment > minimumStartMoment ? SimpleDate(endDate.year, endDate.month, endDate.date) : null),
    endTime
  }));
};
export default ScheduleRecord;